import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, CreditCard, X, Save, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { fmtDate } from '@/lib/dateFmt';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const METODI = [
  { v: 'bonifico', label: 'Bonifico' },
  { v: 'contanti', label: 'Contanti' },
  { v: 'assegno', label: 'Assegno' },
  { v: 'carta', label: 'Carta' },
  { v: 'rid', label: 'RID / SDD' },
  { v: 'compensazione', label: 'Compensazione' },
  { v: 'altro', label: 'Altro' },
];

const EMPTY_FORM = {
  importo: '',
  data: new Date().toISOString().slice(0, 10),
  metodo: 'bonifico',
  riferimento: '',
  note: '',
};

/**
 * Dialog riutilizzabile per la gestione dei pagamenti parziali su una fattura.
 *
 * @param {Object} props
 * @param {boolean} props.open - se il dialog è aperto
 * @param {Function} props.onOpenChange - callback open state
 * @param {Object} props.fattura - la fattura su cui aggiungere pagamenti (deve avere id, importo)
 * @param {'fatture-entrata'|'fatture-uscita'} props.tipo - tipo endpoint
 * @param {Function} props.onUpdated - callback chiamata quando lo stato cambia
 */
export default function PagamentiDialog({ open, onOpenChange, fattura, tipo, onUpdated }) {
  const [pagamenti, setPagamenti] = useState([]);
  const [totalePagato, setTotalePagato] = useState(0);
  const [residuo, setResiduo] = useState(0);
  const [stato, setStato] = useState('');
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const importoTotale = parseFloat(fattura?.importo) || 0;
  const fatturaLabel = tipo === 'fatture-uscita' ? 'incassi' : 'pagamenti';
  const verbo = tipo === 'fatture-uscita' ? 'incasso' : 'pagamento';

  const fetchPagamenti = useCallback(async () => {
    if (!fattura?.id) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/${tipo}/${fattura.id}/pagamenti`, { withCredentials: true });
      setPagamenti(r.data?.pagamenti || []);
      setTotalePagato(r.data?.totale_pagato || 0);
      setResiduo(r.data?.residuo ?? importoTotale);
      setStato(r.data?.stato || '');
    } catch {
      toast.error(`Errore caricamento ${fatturaLabel}`);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fattura?.id, tipo]);

  useEffect(() => {
    if (open && fattura?.id) {
      fetchPagamenti();
      setEditingId(null);
      setForm({ ...EMPTY_FORM, importo: '' });
    }
  }, [open, fattura?.id, fetchPagamenti]);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    const importoNum = parseFloat(form.importo);
    if (!importoNum || importoNum <= 0) {
      toast.error('Importo deve essere maggiore di zero');
      return;
    }
    setBusy(true);
    try {
      const payload = {
        importo: importoNum,
        data: form.data,
        metodo: form.metodo,
        riferimento: form.riferimento || '',
        note: form.note || '',
      };
      if (editingId) {
        await axios.put(`${API}/${tipo}/${fattura.id}/pagamenti/${editingId}`, payload, { withCredentials: true });
        toast.success(`${verbo.charAt(0).toUpperCase() + verbo.slice(1)} aggiornato`);
      } else {
        await axios.post(`${API}/${tipo}/${fattura.id}/pagamenti`, payload, { withCredentials: true });
        toast.success(`${verbo.charAt(0).toUpperCase() + verbo.slice(1)} registrato`);
      }
      setForm({ ...EMPTY_FORM });
      setEditingId(null);
      await fetchPagamenti();
      if (onUpdated) onUpdated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Errore salvataggio ${verbo}`);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(`Eliminare questo ${verbo}?`)) return;
    try {
      await axios.delete(`${API}/${tipo}/${fattura.id}/pagamenti/${id}`, { withCredentials: true });
      toast.success(`${verbo.charAt(0).toUpperCase() + verbo.slice(1)} eliminato`);
      await fetchPagamenti();
      if (onUpdated) onUpdated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore eliminazione');
    }
  };

  const handleEdit = (p) => {
    setEditingId(p.id);
    setForm({
      importo: String(p.importo),
      data: p.data || new Date().toISOString().slice(0, 10),
      metodo: p.metodo || 'bonifico',
      riferimento: p.riferimento || '',
      note: p.note || '',
    });
  };

  const handleSaldaResiduo = () => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM, importo: String(residuo) });
  };

  const statoBadgeClass = {
    da_pagare: 'bg-amber-50 text-amber-700 border-amber-200',
    parzialmente_pagata: 'bg-blue-50 text-blue-700 border-blue-200',
    pagata: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    emessa: 'bg-amber-50 text-amber-700 border-amber-200',
    parzialmente_incassata: 'bg-blue-50 text-blue-700 border-blue-200',
    incassata: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  }[stato] || 'bg-slate-50 text-slate-700 border-slate-200';

  const percPagato = importoTotale > 0 ? Math.min(100, Math.round((totalePagato / importoTotale) * 100)) : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[680px] max-h-[90vh] overflow-y-auto" data-testid="pagamenti-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-indigo-600" />
            {tipo === 'fatture-uscita' ? 'Incassi fattura' : 'Pagamenti fattura'} — {fattura?.numero || ''}
          </DialogTitle>
          <DialogDescription>
            {tipo === 'fatture-uscita' ? 'Cliente' : 'Fornitore'}: <b>{fattura?.fornitore || fattura?.cliente || '-'}</b> · Importo totale: <b>€ {importoTotale.toLocaleString('it-IT', {minimumFractionDigits: 2})}</b>
          </DialogDescription>
        </DialogHeader>

        {/* Riepilogo */}
        <div className="bg-slate-50 border rounded-lg p-3 space-y-2" data-testid="pagamenti-summary">
          <div className="flex justify-between items-center flex-wrap gap-2">
            <Badge variant="outline" className={statoBadgeClass}>{stato || 'caricamento...'}</Badge>
            <p className="text-xs text-slate-500">{pagamenti.length} {fatturaLabel} registrat{pagamenti.length === 1 ? 'o' : 'i'}</p>
          </div>
          <div className="space-y-1.5">
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">Totale {tipo === 'fatture-uscita' ? 'incassato' : 'pagato'}</span>
              <span className="font-mono font-semibold text-slate-900" data-testid="totale-pagato">€ {totalePagato.toLocaleString('it-IT', {minimumFractionDigits: 2})}</span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
              <div className={`h-full transition-all ${percPagato >= 100 ? 'bg-emerald-500' : 'bg-blue-500'}`} style={{ width: `${percPagato}%` }} />
            </div>
            <div className="flex justify-between text-sm border-t pt-2">
              <span className="text-slate-600">Residuo da {tipo === 'fatture-uscita' ? 'incassare' : 'pagare'}</span>
              <span className={`font-mono font-bold ${residuo <= 0.01 ? 'text-emerald-700' : 'text-red-600'}`} data-testid="residuo">
                € {residuo.toLocaleString('it-IT', {minimumFractionDigits: 2})}
              </span>
            </div>
          </div>
        </div>

        {/* Form aggiungi/modifica */}
        {residuo > 0.01 || editingId ? (
          <form onSubmit={handleSubmit} className="bg-blue-50/40 border border-blue-200 rounded-lg p-3 space-y-3" data-testid="pagamento-form">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold text-blue-900">
                {editingId ? `Modifica ${verbo}` : `Nuovo ${verbo}`}
              </Label>
              {!editingId && residuo > 0.01 && (
                <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={handleSaldaResiduo} data-testid="salda-residuo">
                  Salda residuo (€ {residuo.toLocaleString('it-IT', {minimumFractionDigits: 2})})
                </Button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Importo *</Label>
                <Input
                  type="number" step="0.01" min="0.01"
                  value={form.importo}
                  onChange={(e) => setForm({...form, importo: e.target.value})}
                  required
                  className="font-mono"
                  data-testid="form-importo"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Data *</Label>
                <Input
                  type="date"
                  value={form.data}
                  onChange={(e) => setForm({...form, data: e.target.value})}
                  required
                  data-testid="form-data"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Metodo *</Label>
                <Select value={form.metodo} onValueChange={(v) => setForm({...form, metodo: v})}>
                  <SelectTrigger data-testid="form-metodo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {METODI.map(m => <SelectItem key={m.v} value={m.v}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Riferimento</Label>
                <Input
                  value={form.riferimento}
                  onChange={(e) => setForm({...form, riferimento: e.target.value})}
                  placeholder="n. CRO, n. assegno…"
                  data-testid="form-riferimento"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Note</Label>
              <Textarea
                rows={2}
                value={form.note}
                onChange={(e) => setForm({...form, note: e.target.value})}
                placeholder={`Note ${verbo} (opzionale)`}
                className="text-sm"
              />
            </div>
            <div className="flex justify-end gap-2">
              {editingId && (
                <Button type="button" variant="outline" size="sm" onClick={() => { setEditingId(null); setForm(EMPTY_FORM); }}>
                  <X className="h-4 w-4 mr-1" />Annulla
                </Button>
              )}
              <Button type="submit" disabled={busy} className="bg-blue-600 hover:bg-blue-700 text-white" size="sm" data-testid="form-save">
                {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : (editingId ? <Save className="h-4 w-4 mr-1" /> : <Plus className="h-4 w-4 mr-1" />)}
                {busy ? 'Salvo…' : editingId ? 'Aggiorna' : 'Registra'}
              </Button>
            </div>
          </form>
        ) : (
          <div className="text-center py-3 text-emerald-700 text-sm font-medium bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="fattura-completata">
            ✓ Fattura {tipo === 'fatture-uscita' ? 'completamente incassata' : 'completamente pagata'}
          </div>
        )}

        {/* Storico */}
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-slate-700">Storico {fatturaLabel}</h4>
          {loading && <div className="text-xs text-slate-400">Caricamento…</div>}
          {!loading && pagamenti.length === 0 && (
            <p className="text-xs text-slate-400 italic">Nessun {verbo} ancora registrato.</p>
          )}
          {!loading && pagamenti.map((p) => (
            <div key={p.id} className="flex items-center justify-between gap-2 p-2.5 bg-white border rounded-lg hover:border-blue-300 transition-colors" data-testid={`pagamento-row-${p.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono font-semibold text-emerald-700">€ {(p.importo || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})}</span>
                  <Badge variant="outline" className="text-[10px] bg-slate-50">{METODI.find(m => m.v === p.metodo)?.label || p.metodo}</Badge>
                  <span className="text-xs text-slate-500">{fmtDate(p.data, 'dd-MM-yyyy')}</span>
                </div>
                {(p.riferimento || p.note) && (
                  <p className="text-[11px] text-slate-500 mt-0.5 truncate">
                    {p.riferimento && <span className="font-mono">{p.riferimento}</span>}
                    {p.riferimento && p.note && ' · '}
                    {p.note}
                  </p>
                )}
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleEdit(p)} data-testid={`edit-pagamento-${p.id}`}>
                  <CreditCard className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:bg-red-50" onClick={() => handleDelete(p.id)} data-testid={`delete-pagamento-${p.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
